import { dicomWriter } from './dicomWriter';

const MANIFEST_FILE_NAME = 'casedetails.xml';
const SOP_CLASS_VL_ENDOSCOPIC_IMAGE = '1.2.840.10008.5.1.4.1.1.77.1.1';
const TRANSFER_SYNTAX_JPEG_BASELINE = '1.2.840.10008.1.2.4.50';
const IMPLEMENTATION_CLASS_UID = '2.25.130804522287496231261202697833582545367';
const IMPLEMENTATION_VERSION_NAME = 'RMS_CASE_EXP_1';
const JPEG_QUALITY = 0.92;
const SERIES_NUMBER = 1;

const BODY_PART_KEYWORDS = [
    ['HIP', /\bhips?\b/i],
    ['KNEE', /\bknees?\b/i],
    ['SHOULDER', /\bshoulders?\b/i],
    ['ELBOW', /\belbows?\b/i],
    ['WRIST', /\bwrists?\b/i],
    ['HAND', /\bhands?\b/i],
    ['ANKLE', /\bankles?\b/i],
    ['FOOT', /\bfoot\b|\bfeet\b/i],
    ['SPINE', /\bspine\b|\bspinal\b/i],
];

const directoryOf = (file) => {
    const relativePath = file.webkitRelativePath || '';
    const slash = relativePath.lastIndexOf('/');
    return slash === -1 ? '' : relativePath.slice(0, slash);
};

const baseName = (path) => {
    const normalized = String(path || '').replace(/\\/g, '/');
    return normalized.slice(normalized.lastIndexOf('/') + 1);
};

const isDicomFileName = (name) => /\.dcm$/i.test(name);

const parseStrykerDateTime = (value) => {
    const match = String(value || '').trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
    if (!match) return null;
    const [, month, day, year, hour, minute, second] = match;
    const pad = (n) => String(n).padStart(2, '0');
    return {
        date: `${year}${pad(month)}${pad(day)}`,
        time: hour === undefined ? null : `${pad(hour)}${pad(minute)}${pad(second || 0)}`,
    };
};

const titleCase = (value) => String(value || '')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ');

const deriveBodyPart = (procedure) => {
    const hit = BODY_PART_KEYWORDS.find(([, pattern]) => pattern.test(procedure || ''));
    return hit ? hit[0] : null;
};

const bytesToDecimalString = (bytes) => {
    const digits = [];
    let remaining = Array.from(bytes);
    while (remaining.length && !(remaining.length === 1 && remaining[0] === 0)) {
        let carry = 0;
        const quotient = [];
        remaining.forEach((byte) => {
            const current = carry * 256 + byte;
            const q = Math.floor(current / 10);
            carry = current % 10;
            if (quotient.length || q > 0) quotient.push(q);
        });
        digits.push(carry);
        remaining = quotient;
    }
    return digits.length ? digits.reverse().join('') : '0';
};

const uidFromBytes16 = (bytes) => `2.25.${bytesToDecimalString(bytes.subarray(0, 16))}`;

const uidFromUuid = (uuid) => {
    const hex = String(uuid || '').replace(/[^0-9a-f]/gi, '');
    if (hex.length !== 32) return null;
    const bytes = new Uint8Array(16);
    for (let i = 0; i < 16; i += 1) {
        bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    }
    return uidFromBytes16(bytes);
};

const uidFromText = async (text) => {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return uidFromBytes16(new Uint8Array(digest).subarray(0, 16));
};

const parseCaseDetails = (xmlText) => {
    const doc = new DOMParser().parseFromString(xmlText, 'application/xml');
    if (doc.querySelector('parsererror')) {
        throw new Error('CaseDetails.xml could not be parsed');
    }
    const caseNode = doc.querySelector('Case');
    const patientNode = doc.querySelector('PatientInfo');
    if (!caseNode || !patientNode) {
        throw new Error('CaseDetails.xml is missing Case or PatientInfo');
    }
    const attr = (node, name) => (node ? (node.getAttribute(name) || '').trim() : '');

    const clips = Array.from(doc.querySelectorAll('DataClips > Clip'))
        .filter((clip) => attr(clip, 'type').toLowerCase() === 'image' && attr(clip, 'path'))
        .map((clip, index) => ({
            index,
            path: attr(clip, 'path'),
            fileName: baseName(attr(clip, 'path')),
            startTime: attr(clip, 'startTime'),
        }));

    return {
        caseId: attr(caseNode, 'caseId'),
        deviceId: attr(caseNode, 'deviceId'),
        startTime: attr(caseNode, 'startTime'),
        patientId: attr(patientNode, 'patientId'),
        patientFirstName: attr(patientNode, 'patientFirstName'),
        patientMiddleInitial: attr(patientNode, 'patientMiddleInitial'),
        patientLastName: attr(patientNode, 'patientLastName'),
        surgeon: attr(patientNode, 'surgeon'),
        speciality: attr(patientNode, 'speciality'),
        procedure: attr(patientNode, 'procedure'),
        procedureDate: attr(patientNode, 'procedureDate'),
        birthDate: attr(patientNode, 'birthDate'),
        gender: attr(patientNode, 'gender'),
        studyInstanceUIDFromManifest: attr(patientNode, 'studyinstanceuid'),
        clips,
    };
};

const encodeImageAsJpeg = async (file) => {
    const bitmap = await createImageBitmap(file);
    try {
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        canvas.getContext('2d').drawImage(bitmap, 0, 0);
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
        if (!blob) throw new Error('JPEG encoding failed');
        return {
            width: bitmap.width,
            height: bitmap.height,
            bytes: new Uint8Array(await blob.arrayBuffer()),
        };
    } finally {
        bitmap.close();
    }
};

const buildStudyContext = async (manifest) => {
    const studyInstanceUID = manifest.studyInstanceUIDFromManifest
        || uidFromUuid(manifest.caseId)
        || await uidFromText(`${manifest.caseId}|study`);
    const seriesInstanceUID = await uidFromText(`${manifest.caseId}|stills`);
    const procedureDateTime = parseStrykerDateTime(manifest.procedureDate)
        || parseStrykerDateTime(manifest.startTime)
        || parseStrykerDateTime(manifest.clips[0]?.startTime);
    const birth = parseStrykerDateTime(manifest.birthDate);
    const specialityLabel = titleCase(manifest.speciality) || 'Procedure';

    const patientName = [manifest.patientLastName, manifest.patientFirstName, manifest.patientMiddleInitial]
        .map((part) => part || '')
        .join('^')
        .replace(/\^+$/, '');

    return {
        studyInstanceUID,
        seriesInstanceUID,
        studyDate: procedureDateTime?.date || null,
        studyTime: procedureDateTime?.time || null,
        studyDescription: manifest.procedure || `${specialityLabel} case`,
        seriesDescription: `${specialityLabel} stills`,
        bodyPartExamined: deriveBodyPart(manifest.procedure),
        patientName,
        patientId: manifest.patientId,
        patientBirthDate: birth?.date || null,
        patientSex: /^[MFO]$/i.test(manifest.gender) ? manifest.gender.toUpperCase() : null,
        referringPhysician: manifest.surgeon,
        deviceSerialNumber: manifest.deviceId,
    };
};

const buildInstanceElements = (context, clip, jpeg, sopInstanceUID, instanceNumber) => {
    const contentDateTime = parseStrykerDateTime(clip.startTime);
    const elements = [
        { tag: '00080005', vr: 'CS', value: 'ISO_IR 192' },
        { tag: '00080008', vr: 'CS', value: ['ORIGINAL', 'PRIMARY'] },
        { tag: '00080016', vr: 'UI', value: SOP_CLASS_VL_ENDOSCOPIC_IMAGE },
        { tag: '00080018', vr: 'UI', value: sopInstanceUID },
        { tag: '00080060', vr: 'CS', value: 'ES' },
        { tag: '00080064', vr: 'CS', value: 'WSD' },
        { tag: '00081030', vr: 'LO', value: context.studyDescription },
        { tag: '0008103e', vr: 'LO', value: context.seriesDescription },
        { tag: '00100010', vr: 'PN', value: context.patientName },
        { tag: '00100020', vr: 'LO', value: context.patientId },
        { tag: '0020000d', vr: 'UI', value: context.studyInstanceUID },
        { tag: '0020000e', vr: 'UI', value: context.seriesInstanceUID },
        { tag: '00200011', vr: 'IS', value: String(SERIES_NUMBER) },
        { tag: '00200013', vr: 'IS', value: String(instanceNumber) },
        { tag: '00280002', vr: 'US', value: 3 },
        { tag: '00280004', vr: 'CS', value: 'YBR_FULL_422' },
        { tag: '00280006', vr: 'US', value: 0 },
        { tag: '00280010', vr: 'US', value: jpeg.height },
        { tag: '00280011', vr: 'US', value: jpeg.width },
        { tag: '00280100', vr: 'US', value: 8 },
        { tag: '00280101', vr: 'US', value: 8 },
        { tag: '00280102', vr: 'US', value: 7 },
        { tag: '00280103', vr: 'US', value: 0 },
        { tag: '00282110', vr: 'CS', value: '01' },
        { tag: '00282114', vr: 'CS', value: 'ISO_10918_1' },
    ];

    const optional = (tag, vr, value) => {
        if (value !== null && value !== undefined && value !== '') elements.push({ tag, vr, value });
    };
    optional('00080020', 'DA', context.studyDate);
    optional('00080021', 'DA', context.studyDate);
    optional('00080030', 'TM', context.studyTime);
    optional('00080031', 'TM', context.studyTime);
    optional('00080022', 'DA', contentDateTime?.date);
    optional('00080032', 'TM', contentDateTime?.time);
    optional('00080023', 'DA', contentDateTime?.date);
    optional('00080033', 'TM', contentDateTime?.time);
    optional('00080090', 'PN', context.referringPhysician);
    optional('00100030', 'DA', context.patientBirthDate);
    optional('00100040', 'CS', context.patientSex);
    optional('00180015', 'CS', context.bodyPartExamined);
    optional('00181000', 'LO', context.deviceSerialNumber);

    const uncompressedBytes = jpeg.width * jpeg.height * 3;
    optional('00282112', 'DS', (uncompressedBytes / Math.max(1, jpeg.bytes.length)).toFixed(2));
    return elements;
};

const synthesizeInstance = async (context, manifest, clip, sourceFile, instanceNumber) => {
    const jpeg = await encodeImageAsJpeg(sourceFile);
    const sopInstanceUID = await uidFromText(`${manifest.caseId}|${clip.fileName.toLowerCase()}`);
    const bytes = dicomWriter.writePart10File({
        fileMeta: {
            sopClassUID: SOP_CLASS_VL_ENDOSCOPIC_IMAGE,
            sopInstanceUID,
            transferSyntaxUID: TRANSFER_SYNTAX_JPEG_BASELINE,
            implementationClassUID: IMPLEMENTATION_CLASS_UID,
            implementationVersionName: IMPLEMENTATION_VERSION_NAME,
        },
        elements: buildInstanceElements(context, clip, jpeg, sopInstanceUID, instanceNumber),
        encapsulatedPixelDataFragments: [jpeg.bytes],
    });
    const dicomName = clip.fileName.replace(/\.[^.]+$/, '') + '.dcm';
    return new File([bytes], dicomName, { type: 'application/dicom', lastModified: sourceFile.lastModified });
};

const findCaseExportDirectories = (files) => {
    const byDirectory = new Map();
    files.forEach((file) => {
        const dir = directoryOf(file);
        if (!byDirectory.has(dir)) {
            byDirectory.set(dir, { manifestFile: null, hasDicom: false, filesByName: new Map() });
        }
        const entry = byDirectory.get(dir);
        const lowerName = file.name.toLowerCase();
        if (lowerName === MANIFEST_FILE_NAME) entry.manifestFile = file;
        if (isDicomFileName(file.name)) entry.hasDicom = true;
        entry.filesByName.set(lowerName, file);
    });
    return Array.from(byDirectory.values()).filter((entry) => entry.manifestFile && !entry.hasDicom);
};

export const caseExportSynthesizer = {
    parseCaseDetails,
    expandCaseExports: async (files, onProgress) => {
        const exportDirectories = findCaseExportDirectories(files);
        if (!exportDirectories.length) return files;

        const replaced = new Set();
        const synthesized = [];
        const plans = [];
        for (const entry of exportDirectories) {
            try {
                const manifest = parseCaseDetails(await entry.manifestFile.text());
                const clips = manifest.clips
                    .map((clip) => ({ clip, sourceFile: entry.filesByName.get(clip.fileName.toLowerCase()) }))
                    .filter(({ sourceFile }) => sourceFile && !isDicomFileName(sourceFile.name));
                if (clips.length) plans.push({ manifest, clips });
            } catch (err) {
                console.log('caseExportSynthesizer: skipping manifest', err);
            }
        }

        const total = plans.reduce((sum, plan) => sum + plan.clips.length, 0);
        let done = 0;
        for (const plan of plans) {
            const context = await buildStudyContext(plan.manifest);
            for (const { clip, sourceFile } of plan.clips) {
                try {
                    const dicomFile = await synthesizeInstance(context, plan.manifest, clip, sourceFile, clip.index + 1);
                    synthesized.push(dicomFile);
                    replaced.add(sourceFile);
                } catch (err) {
                    console.log(`caseExportSynthesizer: could not convert ${sourceFile.name}`, err);
                } finally {
                    done += 1;
                    if (onProgress) onProgress(done, total);
                }
            }
        }

        if (!synthesized.length) return files;
        return [...files.filter((file) => !replaced.has(file)), ...synthesized];
    },
};
